import { useCallback, useEffect, useState } from 'react';

import { money } from '../lib/money';

import {
  decideAmbassadorApplication,
  fetchStaffAmbassadorApplication,
  fetchStaffAmbassadorApplications,
  fetchStaffAmbassadorLinks,
  fetchStaffAmbassadorPrizes,
  fetchStaffFile,
  reviewAmbassadorDocument,
  setAmbassadorRate,
  textAmbassadorLink,
  type StaffAmbassadorApplicationRow,
  type StaffAmbassadorApplication,
  type StaffAmbassadorLinks,
} from '../lib/api';
import { QrCode } from '../components/QrCode';
import { HeldShares } from '../components/StaffAmbassadorChecks';

/**
 * The staff Ambassadors page (Anthony, 30 October 2026), for the founder and the operations
 * manager: every Ambassador application by status. Opening one shows its documents (Show photo or
 * PDF), what the automatic check found, Accept or Send back for each, "Chat done" with notes, and
 * Approve, Decline (with a reason the person sees) and Suspend. Nobody is approved without a
 * person pressing Approve after the chat.
 *
 * 31 October 2026: how the face is to be checked ("Check by a person: face not compared
 * automatically" unless they said yes); any note from an earlier decline or removal that matches
 * the applicant; and, to end a suspension, "Lift the suspension" (everything held released) or
 * "Remove for fraud", marking the businesses that were fraudulent, or all of them, whose unpaid
 * shares are cancelled. A date of birth from the ID may be kept on the note, if staff have it.
 *
 * 2 November 2026 (ruling 91), for the owner alone: "Prizes and bonuses", every month's Top
 * Ambassador decision (month, winner, bar met, net) and every first-business bonus credited.
 *
 * 3 November 2026 (ruling 94): "Links and QR codes", for the owner and staff given this area:
 * any approved Ambassador's business sign-up link, check code, check link and a large QR code,
 * with "Copy link", "Read it out" (Ozi says it slowly) and "Text this link to a business" (one
 * text from Ozi, as this person's own action); a pending or declined Ambassador shows their
 * status and no link. For the owner alone, "Individual rate": a rate above the standard one for
 * that Ambassador, applied to new shares from the moment it is set.
 */

function when(at: string | null): string {
  if (!at) return 'not yet';
  return new Date(at).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Failure({ text }: { text: string }): JSX.Element | null {
  if (text === '') return null;
  return (
    <p role="alert" className="border-2 border-paper bg-paper text-ink p-4 rounded-2xl m-0">
      {text}
    </p>
  );
}

const fail =
  (set: (text: string) => void) =>
  (failure: unknown): void =>
    set(failure instanceof Error ? failure.message : 'That did not work.');

export function StaffAmbassadors({
  staffKey,
  by,
  onNews,
  onSay,
  owner = false,
}: {
  staffKey: string;
  by: string;
  onNews: (words: string) => void;
  /** Ozi says these words aloud ("Read it out", ruling 94). Shown as news when not given. */
  onSay?: (words: string) => void;
  /** The owner alone sees "Prizes and bonuses" (ruling 91): the net is a company figure. */
  owner?: boolean;
}): JSX.Element {
  const [groups, setGroups] = useState<
    Awaited<ReturnType<typeof fetchStaffAmbassadorApplications>>['groups'] | null
  >(null);
  const [open, setOpen] = useState<string | null>(null);
  const [problem, setProblem] = useState('');

  const load = useCallback(() => {
    fetchStaffAmbassadorApplications(staffKey)
      .then((found) => {
        setGroups(found.groups);
        setProblem('');
      })
      .catch(fail(setProblem));
  }, [staffKey]);
  useEffect(load, [load]);

  return (
    <section aria-labelledby="ambassadors-heading" className="space-y-6 max-w-3xl">
      <h2 id="ambassadors-heading" className="text-lead font-bold m-0">
        Ambassador applications
      </h2>
      <Failure text={problem} />
      <HeldShares staffKey={staffKey} by={by} onNews={onNews} />
      {owner && <PrizesAndBonuses staffKey={staffKey} />}
      {groups && (
        <LinksAndCodes
          staffKey={staffKey}
          rows={groups.flatMap((group) => group.applications)}
          onNews={onNews}
          onSay={onSay ?? onNews}
          owner={owner}
        />
      )}
      {groups?.map((group) => (
        <section key={group.status} aria-labelledby={`amb-${group.status}`} className="space-y-2">
          <h3 id={`amb-${group.status}`} className="font-bold m-0">
            {group.words} ({group.applications.length})
          </h3>
          {group.applications.length === 0 ? (
            <p className="m-0">None.</p>
          ) : (
            <ul className="m-0 ps-6 space-y-2">
              {group.applications.map((row) => (
                <li key={row.id} className="space-y-2">
                  <p className="m-0">
                    {row.name}, {row.kindWords}, {row.area}. Applied {when(row.agreedAt)}.
                    {row.waitingDocuments > 0
                      ? ` ${row.waitingDocuments} document${row.waitingDocuments === 1 ? '' : 's'} waiting.`
                      : ''}
                  </p>
                  <button
                    type="button"
                    aria-expanded={open === row.id}
                    onClick={() => setOpen(open === row.id ? null : row.id)}
                    className="control border-paper bg-paper/10 text-paper"
                  >
                    {open === row.id ? `Close ${row.name}` : `Open ${row.name}`}
                  </button>
                  {open === row.id && (
                    <Application
                      staffKey={staffKey}
                      id={row.id}
                      by={by}
                      onDone={(words) => {
                        onNews(words);
                        load();
                      }}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </section>
  );
}

/**
 * "Links and QR codes" (ruling 94): every Ambassador by name, searched by name, each opening to
 * their business sign-up link, codes and QR code, or to their status when the link does not work.
 */
function LinksAndCodes({
  staffKey,
  rows,
  onNews,
  onSay,
  owner,
}: {
  staffKey: string;
  rows: StaffAmbassadorApplicationRow[];
  onNews: (words: string) => void;
  onSay: (words: string) => void;
  owner: boolean;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const wanted = search.trim().toLowerCase();
  const shown = rows
    .filter((row) => wanted === '' || row.name.toLowerCase().includes(wanted))
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <section aria-labelledby="links-heading" className="space-y-3">
      <h3 id="links-heading" className="font-bold m-0">
        Links and QR codes
      </h3>
      <p className="m-0">
        An approved Ambassador&rsquo;s business sign-up link, check code and QR code, to give to a
        business yourself so it joins through them and the reward goes to them.
      </p>
      <label htmlFor="links-search" className="block font-bold">
        Search by name
      </label>
      <input
        id="links-search"
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="w-full min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3"
      />
      {shown.length === 0 ? (
        <p className="m-0">
          {rows.length === 0 ? 'Nobody has applied yet.' : 'Nobody by that name.'}
        </p>
      ) : (
        <ul className="list-none m-0 p-0 space-y-3">
          {shown.map((row) => (
            <li key={row.id} className="space-y-2">
              <p className="m-0">
                {row.name}, {row.kindWords}: {row.statusWords}.
              </p>
              <button
                type="button"
                aria-expanded={open === row.id}
                onClick={() => setOpen(open === row.id ? null : row.id)}
                className="control border-paper bg-paper/10 text-paper"
              >
                {open === row.id ? `Hide ${row.name}'s links` : `Show ${row.name}'s links`}
              </button>
              {open === row.id && (
                <AmbassadorLinks
                  staffKey={staffKey}
                  id={row.id}
                  onNews={onNews}
                  onSay={onSay}
                  owner={owner}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** One Ambassador's links, QR code and buttons, or their status and no link. */
function AmbassadorLinks({
  staffKey,
  id,
  onNews,
  onSay,
  owner,
}: {
  staffKey: string;
  id: string;
  onNews: (words: string) => void;
  onSay: (words: string) => void;
  owner: boolean;
}): JSX.Element {
  const [links, setLinks] = useState<StaffAmbassadorLinks | null>(null);
  const [problem, setProblem] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetchStaffAmbassadorLinks(staffKey, id).then(setLinks).catch(fail(setProblem));
  }, [staffKey, id]);
  useEffect(load, [load]);

  if (!links) return <Failure text={problem} />;

  const copy = (): void => {
    if (!links.link) return;
    const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
    if (!clipboard?.writeText) {
      setProblem('Copying is not available here. The link is written above: please select it.');
      return;
    }
    clipboard
      .writeText(links.link)
      .then(() => onNews(`${links.firstName}'s link is copied.`))
      .catch(() => setProblem('The link could not be copied. It is written above.'));
  };

  const text = (): void => {
    setProblem('');
    if (phone.trim() === '') {
      setProblem('Please give the phone number of the business.');
      return;
    }
    setBusy(true);
    textAmbassadorLink(staffKey, id, phone.trim())
      .then((result) => {
        onNews(result.message);
        setPhone('');
      })
      .catch(fail(setProblem))
      .finally(() => setBusy(false));
  };

  return (
    <article
      aria-labelledby={`links-${id}`}
      className="space-y-3 border-2 border-paper rounded-2xl p-4"
    >
      <h4 id={`links-${id}`} className="font-bold m-0">
        {links.firstName}
        {links.surname ? ` ${links.surname}` : ''}: links and codes
      </h4>
      <p className="m-0">Status: {links.statusWords}.</p>
      <Failure text={problem} />
      {!links.link ? (
        <p className="m-0">{links.words}</p>
      ) : (
        <>
          <ul className="m-0 ps-6 space-y-1">
            <li>Ambassador check code: {links.checkCode}</li>
            <li className="break-all">Business sign-up link: {links.link}</li>
            <li className="break-all">Check link: {links.checkLink}</li>
          </ul>
          <QrCode
            value={links.link}
            label={`QR code for ${links.firstName}'s business sign-up link`}
            className="w-64 h-64 max-w-full bg-white"
          />
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={copy} className="control bg-highlight text-on-accent">
              Copy link
            </button>
            <button
              type="button"
              onClick={() =>
                onSay(
                  `${links.firstName}'s business sign-up link, slowly: ${links.linkAloud ?? links.link}.`,
                )
              }
              className="control border-paper bg-paper/10 text-paper"
            >
              Read it out
            </button>
          </div>
          <label htmlFor={`text-phone-${id}`} className="block font-bold">
            The business&rsquo;s mobile number, for one text with the link
          </label>
          <input
            id={`text-phone-${id}`}
            type="tel"
            inputMode="tel"
            autoComplete="off"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            className="w-full min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3"
          />
          <button
            type="button"
            disabled={busy}
            onClick={text}
            className="control bg-highlight text-on-accent disabled:opacity-60"
          >
            Text this link to a business
          </button>
          <p className="m-0">
            The text says who invited them and that joining is always free. The number is written in
            the audit log as your action, and kept nowhere else.
          </p>
        </>
      )}
      {owner && links.approved && (
        <IndividualRate
          staffKey={staffKey}
          id={id}
          name={links.firstName || links.name}
          rate={links.rate}
          onDone={(words) => {
            onNews(words);
            load();
          }}
        />
      )}
    </article>
  );
}

/**
 * "Individual rate" (ruling 94), the owner alone: a rate above the standard one for this
 * Ambassador, as a percentage, applied to new shares from the moment it is set.
 */
function IndividualRate({
  staffKey,
  id,
  name,
  rate,
  onDone,
}: {
  staffKey: string;
  id: string;
  name: string;
  rate: StaffAmbassadorLinks['rate'];
  onDone: (words: string) => void;
}): JSX.Element {
  const [percent, setPercent] = useState(String(rate.percent));
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);
  const save = (): void => {
    setProblem('');
    const value = Number(percent);
    if (percent.trim() === '' || !Number.isFinite(value)) {
      setProblem('Please give the rate as a percentage, such as 3.');
      return;
    }
    setBusy(true);
    setAmbassadorRate(staffKey, id, value)
      .then((result) => onDone(result.message))
      .catch(fail(setProblem))
      .finally(() => setBusy(false));
  };
  return (
    <section aria-labelledby={`rate-${id}`} className="space-y-2">
      <h5 id={`rate-${id}`} className="font-bold m-0">
        Individual rate
      </h5>
      <p className="m-0">
        {name} earns {rate.words}. You can grant {name} a higher rate than the standard one, as a
        thank you: for example 3% where others earn 2%. It applies to every share on orders from the
        moment you set it, and never changes anything already earned.
      </p>
      <label htmlFor={`rate-percent-${id}`} className="block font-bold">
        {name}&rsquo;s rate, as a percentage
      </label>
      <input
        id={`rate-percent-${id}`}
        type="number"
        inputMode="decimal"
        min={rate.standardPercent}
        step="0.5"
        value={percent}
        onChange={(event) => setPercent(event.target.value)}
        className="w-full min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3"
      />
      <Failure text={problem} />
      <button
        type="button"
        disabled={busy}
        onClick={save}
        className="control bg-highlight text-on-accent disabled:opacity-60"
      >
        Set {name}&rsquo;s rate
      </button>
    </section>
  );
}

/**
 * "Prizes and bonuses" (ruling 91), the owner alone: each month's Top Ambassador decision, and
 * each first-business bonus credited. Read from the owner's money area; nothing to change here.
 */
function PrizesAndBonuses({ staffKey }: { staffKey: string }): JSX.Element {
  const [found, setFound] = useState<Awaited<ReturnType<typeof fetchStaffAmbassadorPrizes>> | null>(
    null,
  );
  const [problem, setProblem] = useState('');
  useEffect(() => {
    fetchStaffAmbassadorPrizes(staffKey).then(setFound).catch(fail(setProblem));
  }, [staffKey]);
  return (
    <section aria-labelledby="prizes-heading" className="space-y-3">
      <h3 id="prizes-heading" className="font-bold m-0">
        Prizes and bonuses
      </h3>
      <Failure text={problem} />
      {found && (
        <>
          <p className="m-0">
            Top Ambassador of the month: {money(found.prizePence)}, paid only in a month whose net
            income from Ambassador-brought businesses is at least {money(found.barPence)}.
          </p>
          {found.prizes.length === 0 ? (
            <p className="m-0">No month has been decided yet.</p>
          ) : (
            <table className="w-full text-left">
              <caption className="visually-hidden">
                Each month's Top Ambassador decision: the winner, whether the bar was met and the
                net income
              </caption>
              <thead>
                <tr>
                  <th scope="col">Month</th>
                  <th scope="col">Winner</th>
                  <th scope="col">Businesses</th>
                  <th scope="col">Bar</th>
                  <th scope="col">Net</th>
                  <th scope="col">Paid</th>
                </tr>
              </thead>
              <tbody>
                {found.prizes.map((row) => (
                  <tr key={row.month}>
                    <th scope="row" className="font-normal">
                      {row.monthWords}
                    </th>
                    <td>{row.winnerName ?? 'Nobody'}</td>
                    <td>{row.qualifyingBusinesses}</td>
                    <td>{row.barMet ? 'Met' : 'Not met'}</td>
                    <td>{row.netWords}</td>
                    <td>{row.prizePence > 0 ? money(row.prizePence) : 'No prize'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <h4 className="font-bold m-0">First-business bonuses credited</h4>
          {found.firstBusinessBonuses.length === 0 ? (
            <p className="m-0">None yet.</p>
          ) : (
            <ul className="m-0 ps-6 space-y-1">
              {found.firstBusinessBonuses.map((row) => (
                <li key={`${row.ambassadorName}-${row.at}`}>
                  {row.ambassadorName}: {row.amountWords} for {row.business}, {when(row.at)}.
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function Application({
  staffKey,
  id,
  by,
  onDone,
}: {
  staffKey: string;
  id: string;
  by: string;
  onDone: (words: string) => void;
}): JSX.Element {
  const [application, setApplication] = useState<StaffAmbassadorApplication | null>(null);
  const [problem, setProblem] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [born, setBorn] = useState('');
  const [fraudShops, setFraudShops] = useState<string[]>([]);
  const [allShops, setAllShops] = useState(false);

  const load = useCallback(() => {
    fetchStaffAmbassadorApplication(staffKey, id)
      .then((found) => {
        setApplication(found.application);
        setNotes(found.application.chatNotes);
      })
      .catch(fail(setProblem));
  }, [staffKey, id]);
  useEffect(load, [load]);

  if (!application) return <Failure text={problem} />;

  const act = (
    action: 'chat-done' | 'approve' | 'decline' | 'suspend' | 'lift' | 'remove-for-fraud',
  ): void => {
    setProblem('');
    const needsReason =
      action === 'decline' || action === 'suspend' || action === 'remove-for-fraud';
    if (needsReason && reason.trim() === '') {
      setProblem('Please give the reason they will see.');
      return;
    }
    if (action === 'remove-for-fraud' && !allShops && fraudShops.length === 0) {
      setProblem('Please mark the businesses that were fraudulent, or choose all of them.');
      return;
    }
    decideAmbassadorApplication(staffKey, id, action, {
      by,
      ...(action === 'chat-done' ? { notes: notes.trim() } : {}),
      ...(needsReason ? { reason: reason.trim() } : {}),
      ...((action === 'decline' || action === 'remove-for-fraud') && born !== ''
        ? { dateOfBirth: born }
        : {}),
      ...(action === 'remove-for-fraud' ? { shops: allShops ? ('all' as const) : fraudShops } : {}),
    })
      .then((result) => {
        onDone(result.message);
        load();
      })
      .catch(fail(setProblem));
  };

  const heading = `${application.name}'s application`;
  return (
    <article
      aria-labelledby={`app-${id}`}
      className="space-y-4 border-2 border-paper rounded-2xl p-4"
    >
      <h4 id={`app-${id}`} className="font-bold m-0">
        {heading}
      </h4>
      <ul className="m-0 ps-6 space-y-1">
        <li>Status: {application.statusWords}</li>
        <li>
          Agreed to the terms {when(application.agreedAt)} (version {application.termsVersion})
        </li>
        <li>Documents sent: {when(application.documentsSentAt)}</li>
        <li>Documents checked: {when(application.documentsCheckedAt)}</li>
        <li>
          Chat:{' '}
          {application.chatDoneAt
            ? `done ${when(application.chatDoneAt)}, by ${application.chatDoneBy ?? 'staff'}`
            : 'not yet'}
        </li>
        {application.faceCheck && <li>Face photo: {application.faceCheck}</li>}
        {application.decisionReason && <li>Reason given: {application.decisionReason}</li>}
        {application.stillNeeded.length > 0 && (
          <li>Still needed: {application.stillNeeded.map((row) => row.name).join(', ')}</li>
        )}
      </ul>
      {(application.identityMatches ?? []).length > 0 && (
        <section aria-label={`Earlier notes that match ${application.name}`} className="space-y-1">
          <p className="m-0 font-bold">
            Please check: an earlier note matches this name or account.
          </p>
          <ul className="m-0 ps-6 space-y-1">
            {(application.identityMatches ?? []).map((match) => (
              <li key={match.id}>{match.words}</li>
            ))}
          </ul>
        </section>
      )}
      <Failure text={problem} />

      <h5 className="font-bold m-0">Documents</h5>
      {application.documents.length === 0 ? (
        <p className="m-0">Nothing sent yet.</p>
      ) : (
        <ul className="list-none m-0 p-0 space-y-4">
          {application.documents.map((document) => (
            <li key={document.id}>
              <DocumentReview
                staffKey={staffKey}
                document={document}
                who={application.name}
                by={by}
                onDone={(words) => {
                  onDone(words);
                  load();
                }}
              />
            </li>
          ))}
        </ul>
      )}

      <h5 className="font-bold m-0">The chat with the founder</h5>
      <label htmlFor={`notes-${id}`} className="block font-bold">
        Notes from the chat
      </label>
      <textarea
        id={`notes-${id}`}
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        className="w-full min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3"
      />
      <button
        type="button"
        disabled={!application.canRecordChat}
        onClick={() => act('chat-done')}
        className="control bg-highlight text-on-accent disabled:opacity-60"
      >
        Chat done
      </button>
      {!application.canRecordChat && application.status !== 'approved' && (
        <p className="m-0">The chat can be recorded once every document is checked.</p>
      )}

      <h5 className="font-bold m-0">Decision</h5>
      <label htmlFor={`reason-${id}`} className="block font-bold">
        Reason, for Decline, Suspend or Remove for fraud (they see it)
      </label>
      <textarea
        id={`reason-${id}`}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        className="w-full min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3"
      />
      <label htmlFor={`born-${id}`} className="block font-bold">
        Date of birth on their ID, for the note kept after Decline or Remove for fraud (only if you
        have it)
      </label>
      <input
        id={`born-${id}`}
        type="date"
        value={born}
        onChange={(event) => setBorn(event.target.value)}
        className="w-full min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3"
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!application.canApprove}
          onClick={() => act('approve')}
          className="control bg-highlight text-on-accent disabled:opacity-60"
        >
          Approve
        </button>
        {application.canDecline !== false && (
          <button
            type="button"
            onClick={() => act('decline')}
            className="control border-paper bg-paper/10 text-paper"
          >
            Decline
          </button>
        )}
        {application.canSuspend !== false && (
          <button
            type="button"
            onClick={() => act('suspend')}
            className="control border-paper bg-paper/10 text-paper"
          >
            Suspend
          </button>
        )}
      </div>

      {(application.canLift || application.canRemoveForFraud) && (
        <section aria-labelledby={`end-${id}`} className="space-y-3">
          <h5 id={`end-${id}`} className="font-bold m-0">
            End the suspension
          </h5>
          <p className="m-0">
            Lift it if there was no problem: everything they earned from real, completed orders is
            released. Or remove them for fraud: the unpaid shares from the businesses you mark are
            cancelled, and they can never apply again.
          </p>
          {application.canLift && (
            <button
              type="button"
              onClick={() => act('lift')}
              className="control bg-highlight text-on-accent"
            >
              Lift the suspension
            </button>
          )}
          {application.canRemoveForFraud && (
            <fieldset className="space-y-2 border-2 border-paper/40 rounded-2xl p-3 m-0">
              <legend className="font-bold">Which businesses were fraudulent?</legend>
              <div className="flex items-center gap-3 min-h-control">
                <input
                  type="checkbox"
                  id={`fraud-all-${id}`}
                  checked={allShops}
                  onChange={(event) => setAllShops(event.target.checked)}
                  className="h-6 w-6"
                />
                <label htmlFor={`fraud-all-${id}`} className="m-0">
                  All of them: cancel every unpaid share
                </label>
              </div>
              {(application.businesses ?? []).map((business) => (
                <div key={business.id} className="flex items-center gap-3 min-h-control">
                  <input
                    type="checkbox"
                    id={`fraud-${id}-${business.id}`}
                    checked={allShops || fraudShops.includes(business.id)}
                    disabled={allShops}
                    onChange={(event) =>
                      setFraudShops((now) =>
                        event.target.checked
                          ? [...now, business.id]
                          : now.filter((one) => one !== business.id),
                      )
                    }
                    className="h-6 w-6"
                  />
                  <label htmlFor={`fraud-${id}-${business.id}`} className="m-0">
                    {business.name}: {money(business.unpaidPence)} unpaid
                  </label>
                </div>
              ))}
              <button
                type="button"
                onClick={() => act('remove-for-fraud')}
                className="control border-paper bg-paper/10 text-paper"
              >
                Remove for fraud
              </button>
            </fieldset>
          )}
        </section>
      )}
    </article>
  );
}

function DocumentReview({
  staffKey,
  document,
  who,
  by,
  onDone,
}: {
  staffKey: string;
  document: StaffAmbassadorApplication['documents'][number];
  who: string;
  by: string;
  onDone: (words: string) => void;
}): JSX.Element {
  const [file, setFile] = useState('');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState('');
  const pdf = document.contentType === 'application/pdf';
  const decided = document.status !== 'submitted';

  const decide = (decision: 'accept' | 'reject'): void => {
    setProblem('');
    reviewAmbassadorDocument(staffKey, document.id, {
      decision,
      by,
      ...(note.trim() ? { note: note.trim() } : {}),
    })
      .then(() =>
        onDone(
          `${document.name}, from ${who}: ${decision === 'accept' ? 'accepted' : 'sent back'}.`,
        ),
      )
      .catch(fail(setProblem));
  };

  return (
    <section
      aria-label={`${document.name}, from ${who}`}
      className="space-y-2 border-2 border-paper/40 rounded-2xl p-3"
    >
      <p className="m-0 font-bold">
        {document.name}:{' '}
        {document.status === 'accepted'
          ? 'checked'
          : document.status === 'rejected'
            ? 'sent back'
            : 'waiting'}
      </p>
      {document.checkedAt && (
        <p className="m-0">
          The automatic check:{' '}
          {document.checkPassed ? 'clearly fine.' : (document.checkNote ?? 'not sure.')}
          {document.checkName ? ` Name read: ${document.checkName}.` : ''}
          {document.checkIssuedOn ? ` Dated ${document.checkIssuedOn}.` : ''}
        </p>
      )}
      {document.reviewedBy && <p className="m-0">Decided by {document.reviewedBy}.</p>}
      {document.shareCode ? (
        <p className="m-0">
          Share code: {document.shareCode}. Check it on the government website before accepting.
        </p>
      ) : !document.hasFile ? (
        <p className="m-0">The file has been removed.</p>
      ) : file && pdf ? (
        <a
          href={file}
          target="_blank"
          rel="noreferrer"
          className="control border-paper bg-paper/10 text-paper"
        >
          Open the PDF of {document.name}, from {who}
        </a>
      ) : file ? (
        <img src={file} alt={`${document.name}, from ${who}`} className="max-w-full" />
      ) : (
        <button
          type="button"
          onClick={() => {
            fetchStaffFile(
              staffKey,
              `/staff/ambassador-documents/${encodeURIComponent(document.id)}/file`,
            )
              .then(setFile)
              .catch(() => setProblem('The file could not be opened.'));
          }}
          className="control border-paper bg-paper/10 text-paper"
        >
          {pdf ? 'Show PDF' : 'Show photo'}
        </button>
      )}
      <Failure text={problem} />
      {!decided && (
        <>
          <label htmlFor={`note-${document.id}`} className="block font-bold">
            Note (needed when sending it back, so they know what to send instead)
          </label>
          <textarea
            id={`note-${document.id}`}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className="w-full min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => decide('accept')}
              className="control bg-highlight text-on-accent"
            >
              Accept
            </button>
            <button
              type="button"
              onClick={() => decide('reject')}
              className="control border-paper bg-paper/10 text-paper"
            >
              Send back
            </button>
          </div>
        </>
      )}
    </section>
  );
}
